"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { DocumentWithWebkitFS } from "@/types/dom";
import type { DemoView } from "@/types/demo";
import { DEMO_SECTIONS } from "@/app/coba/_components/demo-data";
import { calcResult } from "@/app/coba/_components/utils";
import { OverviewContent } from "@/app/coba/_components/overview-content";
import { ResultContent } from "@/app/coba/_components/result-content";
import { ExamContent } from "@/app/coba/_components/exam-content";
import { useDemoExamSecurity } from "@/hooks/use-exam-security";
import { Card } from "@/components/ui/card";

export function DemoExam() {
  const router = useRouter();
  const [view, setView] = useState<DemoView>("overview");
  const [sectionIdx, setSectionIdx] = useState(0);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [flags, setFlags] = useState<Set<string>>(new Set());
  const [timer, setTimer] = useState(
    DEMO_SECTIONS.reduce((s, sec) => s + sec.durationMinutes, 0) * 60,
  );
  const [showNav, setShowNav] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [showReview, setShowReview] = useState(false);

  // Anti-cheat / fullscreen cluster lives in useExamSecurity; the hook owns
  // the penalty state internally and applies the -10s penalty to `timer`.
  const {
    requestFullscreen,
    exitFullscreen,
    tabSwitchCount,
    setTabSwitchCount,
    penaltyFlash,
    penaltyMessage,
    tabWarning,
    setTabWarning,
    showFullscreenWarning,
    setShowFullscreenWarning,
    isWindowBlurred,
  } = useDemoExamSecurity({ view, setTimer });

  const totalQuestions = DEMO_SECTIONS.flatMap((s) => s.questions).length;
  // Idx state is clamped by goPrev/goNext and initialized to 0 — DEMO_SECTIONS is a constant import.
  const section = DEMO_SECTIONS[sectionIdx]!;
  const question = section.questions[questionIdx]!;

  // Timer
  useEffect(() => {
    if (view !== "exam") return;
    timerRef.current = setInterval(() => {
      setTimer((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [view]);

  // Auto-submit on timer end
  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- handleSubmit is a const declared later in the component; the effect runs post-render so the closure is bound by then. Reordering would disturb the demo-exam timer/auto-submit flow.
    if (view === "exam" && timer <= 0) handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer, view]);

  const getGlobalIdx = (si: number, qi: number) => {
    let n = 0;
    for (let i = 0; i < si; i++) n += DEMO_SECTIONS[i]!.questions.length;
    return n + qi + 1;
  };

  const globalIdx = getGlobalIdx(sectionIdx, questionIdx);
  const isFirst = sectionIdx === 0 && questionIdx === 0;
  const isLast =
    sectionIdx === DEMO_SECTIONS.length - 1 &&
    questionIdx === section.questions.length - 1;
  const answeredCount = Object.keys(answers).length;
  const isUrgent = timer <= 60;

  const selectOption = (key: string) => {
    setAnswers((prev) =>
      prev[question.id] === key
        ? (() => {
            const n = { ...prev };
            delete n[question.id];
            return n;
          })()
        : { ...prev, [question.id]: key },
    );
  };
  const toggleFlag = () =>
    setFlags((prev) => {
      const n = new Set(prev);
      if (n.has(question.id)) {
        n.delete(question.id);
      } else {
        n.add(question.id);
      }
      return n;
    });
  const demoMainRef = useRef<HTMLElement>(null);
  const scrollTop = () => demoMainRef.current?.scrollTo({ top: 0 });
  const goPrev = () => {
    if (questionIdx > 0) setQuestionIdx(questionIdx - 1);
    else if (sectionIdx > 0) {
      setSectionIdx(sectionIdx - 1);
      setQuestionIdx(DEMO_SECTIONS[sectionIdx - 1]!.questions.length - 1);
    }
    scrollTop();
  };
  const goNext = () => {
    if (questionIdx < section.questions.length - 1) setQuestionIdx(questionIdx + 1);
    else if (sectionIdx < DEMO_SECTIONS.length - 1) {
      setSectionIdx(sectionIdx + 1);
      setQuestionIdx(0);
    }
    scrollTop();
  };
  const goTo = (si: number, qi: number) => {
    setSectionIdx(si);
    setQuestionIdx(qi);
    setShowNav(false);
    scrollTop();
  };

  const handleSubmit = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setShowSubmitConfirm(false);
    exitFullscreen();
    setView("result");
  };

  const startExam = () => {
    setAnswers({});
    setFlags(new Set());
    setSectionIdx(0);
    setQuestionIdx(0);
    setTabSwitchCount(0);
    setTimer(DEMO_SECTIONS.reduce((s, sec) => s + sec.durationMinutes, 0) * 60);
    setView("exam");
  };

  const handleClose = () => {
    try {
      const fse =
        document.fullscreenElement ||
        (document as DocumentWithWebkitFS).webkitFullscreenElement;
      if (fse) {
        const exit =
          document.exitFullscreen ??
          (document as DocumentWithWebkitFS).webkitExitFullscreen;
        if (exit) exit.call(document).catch(() => {});
      }
    } catch {}
    router.push("/");
  };

  // ===== OVERVIEW =====
  if (view === "overview") {
    const totalDuration = DEMO_SECTIONS.reduce((s, sec) => s + sec.durationMinutes, 0);
    return (
      <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm">
        {/* Desktop: centered modal */}
        {/* Mobile: bottom sheet */}
        <div
          className="hidden h-full overflow-y-auto px-4 py-6 sm:block"
          onClick={handleClose}
        >
          <Card
            onClick={(e) => e.stopPropagation()}
            className="relative mx-auto w-full max-w-lg p-6"
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Tutup"
              onClick={handleClose}
              className="absolute top-4 right-4"
            >
              <X size={18} />
            </Button>
            <OverviewContent
              totalDuration={totalDuration}
              totalQuestions={totalQuestions}
              onStart={startExam}
            />
          </Card>
        </div>
        <div className="flex h-full flex-col sm:hidden" onClick={handleClose}>
          <div className="flex-1" />
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-card max-h-[90vh] overflow-y-auto rounded-t-[22px] px-5 pt-3 pb-8"
          >
            <div className="bg-foreground/10 mx-auto mb-2 h-1 w-10 rounded-full" />
            <div className="mb-3 flex items-center justify-between">
              <span className="text-foreground/30 text-xs font-bold">Simulasi Ujian</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Tutup"
                onClick={handleClose}
              >
                <X size={18} />
              </Button>
            </div>
            <OverviewContent
              totalDuration={totalDuration}
              totalQuestions={totalQuestions}
              onStart={startExam}
            />
          </div>
        </div>
      </div>
    );
  }

  // ===== RESULT =====
  if (view === "result") {
    const result = calcResult(DEMO_SECTIONS, answers);
    return (
      <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm">
        {/* Desktop: centered modal */}
        <div
          className="hidden h-full overflow-y-auto px-4 py-8 sm:block"
          onClick={handleClose}
        >
          <Card
            className="relative mx-auto w-full max-w-md gap-0 overflow-hidden py-0"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type="button"
              variant="glass"
              size="icon"
              onClick={handleClose}
              className="absolute top-3 right-3 z-20"
            >
              <X size={16} />
            </Button>
            <ResultContent
              result={result}
              answers={answers}
              tabSwitchCount={tabSwitchCount}
              showReview={showReview}
              setShowReview={setShowReview}
              onRetry={startExam}
              onClose={handleClose}
            />
          </Card>
        </div>
        {/* Mobile: bottom sheet */}
        <div className="flex h-full flex-col sm:hidden">
          <div className="flex-1" onClick={handleClose} />
          <div className="bg-card max-h-[92vh] overflow-y-auto rounded-t-[22px]">
            <div className="bg-card sticky top-0 z-10 px-5 pt-3 pb-1">
              <div className="bg-foreground/10 mx-auto mb-2 h-1 w-10 rounded-full" />
              <div className="flex items-center justify-between">
                <span className="text-foreground/30 text-xs font-bold">
                  Hasil Simulasi
                </span>
                <Button type="button" variant="ghost" size="icon" onClick={handleClose}>
                  <X size={18} />
                </Button>
              </div>
            </div>
            <ResultContent
              result={result}
              answers={answers}
              tabSwitchCount={tabSwitchCount}
              showReview={showReview}
              setShowReview={setShowReview}
              onRetry={startExam}
              onClose={handleClose}
            />
          </div>
        </div>
      </div>
    );
  }

  // ===== EXAM (exact replica of real exam page) =====
  return (
    <ExamContent
      isWindowBlurred={isWindowBlurred}
      penaltyFlash={penaltyFlash}
      penaltyMessage={penaltyMessage}
      timer={timer}
      isUrgent={isUrgent}
      showNav={showNav}
      tabWarning={tabWarning}
      tabSwitchCount={tabSwitchCount}
      showFullscreenWarning={showFullscreenWarning}
      showSubmitConfirm={showSubmitConfirm}
      answers={answers}
      flags={flags}
      sectionIdx={sectionIdx}
      questionIdx={questionIdx}
      section={section}
      question={question}
      globalIdx={globalIdx}
      isFirst={isFirst}
      isLast={isLast}
      answeredCount={answeredCount}
      totalQuestions={totalQuestions}
      demoMainRef={demoMainRef}
      setShowSubmitConfirm={setShowSubmitConfirm}
      setShowNav={setShowNav}
      setTabWarning={setTabWarning}
      setShowFullscreenWarning={setShowFullscreenWarning}
      toggleFlag={toggleFlag}
      selectOption={selectOption}
      goPrev={goPrev}
      goNext={goNext}
      goTo={goTo}
      getGlobalIdx={getGlobalIdx}
      requestFullscreen={requestFullscreen}
      handleSubmit={handleSubmit}
    />
  );
}
